import { ArrowLeftOutlined } from '@ant-design/icons'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { setGoBackToVehicleTest } from '../actions/go-to-vehicle-test'
import { Button, Row, Col, ConfigProvider, theme, Flex, Typography } from 'antd'
import ModalFullScreen from '../components/Modal/ModalFullScreen'
import { Loadable } from '../components/Loading'
import { useSearchSubsystems } from '../BlockDiagram/hooks'
import { FormattedMessage } from 'react-intl'
import { SubsystemsViewTable } from '../components/SubsystemTable'
import {
  BRAND_BLUE,
  WHITE_COLOR,
  COMPONENT_CATEGORY_INLINE,
  COMPONENT_CATEGORY_SPLICE,
} from '../constants'
import history from '../history'
import { unsetRelatedDiagramsModal } from '../actions/related-diagrams-modal-actions'
import { diagramSearchParams } from './deepLink'

const CATEGORIES_NEEDING_PINS = [
  COMPONENT_CATEGORY_INLINE,
  COMPONENT_CATEGORY_SPLICE,
]

export const needsPinsForRelatedDiagrams = (category) =>
  CATEGORIES_NEEDING_PINS.includes(category)

// One look in both themes: a brand-blue fill with a white label.
//
// Both tokens are pinned rather than inherited. The app's dark theme makes primary
// buttons white-on-navy (`ComponentThemeGenerator`), so without this the same
// control would read as two different buttons depending on the theme — and a white
// fill is invisible on the white card of the harness view tooltip, which is the
// other place this button appears.
export const RelatedDiagramsButton = ({
  showModal,
  disabled = false,
  icon = null,
}) => (
  <ConfigProvider
    theme={{
      components: {
        Button: { colorPrimary: BRAND_BLUE, primaryColor: WHITE_COLOR },
      },
    }}
  >
    <Button
      type="primary"
      onClick={showModal}
      disabled={disabled}
      icon={icon}
      data-testid="related-diagrams-button"
    >
      <FormattedMessage id="relatedDiagrams.button" />
    </Button>
  </ConfigProvider>
)

// Same button, on the light palette, for the harness view tooltip — whose card is
// white while the app around it may be dark.
//
// The pinned fill and label above cover the enabled button, but not the disabled
// one: antd takes that from `colorTextDisabled` and `colorBgContainerDisabled`,
// which the algorithm decides. Inherit the dark algorithm here and a disabled
// button becomes a near-transparent box with a near-white label — invisible on a
// white card. So the algorithm still has to be forced, even though the colours are
// pinned.
export const RelatedDiagramsModalButton = (props) => (
  <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
    <RelatedDiagramsButton {...props} />
  </ConfigProvider>
)

// The component's own description, translated by the terminology endpoint where one
// exists. Optional: not every component has a description, and terminology has no
// entry for most component ids, so this renders nothing rather than a placeholder.
const ComponentDescription = ({ description }) =>
  description ? (
    <Typography.Text type="secondary" data-testid="component-description">
      {description}
    </Typography.Text>
  ) : null

// One block per resolved connector. A device or inline pinout selection can span
// several connectors, and each is a diagram of its own — so the group reads in the
// order the user needs it: the pins they picked, then the connector and pins those
// reached, then what that connector is. Leading with the picked pins is deliberate —
// they are the only part the user recognises, and after the jump the app is in the
// connector's context and the picked cavities are never mentioned again.
//
// When resolution was the identity — a connector or power distribution pinout, where
// the pin is already a diagram subject — there is no translation to show, so the
// connector simply names itself.
const ConnectorGroup = ({ connector, subsystems, onSelect }) => (
  <div className="related-diagrams-group" data-testid="related-diagrams-group">
    <SubsystemsViewTable
      susbsystemCallback={onSelect}
      subsystems={subsystems}
      pagination={{ pageSize: 5 }}
      // In the table's own frame rather than floating above it, so a group reads as
      // one thing: these diagrams, for these pins.
      title={
        <Flex align="baseline" gap="small">
          <Typography.Text strong>
            <FormattedMessage
              id={
                connector.fromPins.join(',') === connector.pins.join(',')
                  ? 'relatedDiagrams.group'
                  : 'relatedDiagrams.groupResolved'
              }
              values={{
                fromCount: connector.fromPins.length,
                fromPins: connector.fromPins.join(', '),
                alias: connector.connectorAlias || connector.connectorId,
                count: connector.pins.length,
                pins: connector.pins.join(', '),
              }}
            />
          </Typography.Text>
          <ComponentDescription description={connector.connectorDescription} />
        </Flex>
      }
      // Resolved, but this connector is in no subsystem — the common case on
      // sparsely mapped data. Saying so in the table's own empty state keeps the
      // pins named above it, which a standalone Empty could not.
      emptyText={<FormattedMessage id="relatedDiagrams.groupNoDiagrams" />}
    />
  </div>
)

export const RelatedDiagramsModal = () => {
  const dispatch = useDispatch()
  const { isOpen, vin, componentId, pins } = useSelector(
    (state) => state.relatedDiagramsModal,
    shallowEqual,
  )

  const { searchSubsystems, component, connectors, unresolved, isLoading } =
    useSearchSubsystems({
      vin: vin,
      componentId: componentId,
      pins: pins || [],
    })

  const header = () => {
    return (
      <>
        <Row>
          <Col span={10}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => dispatch(unsetRelatedDiagramsModal())}
            >
              <FormattedMessage id="generic.back" />
            </Button>
          </Col>
          <Col span={10}>
            <Flex align="baseline" gap="small">
              <FormattedMessage
                id={
                  pins?.length > 0
                    ? 'relatedDiagrams.modalWithPins'
                    : 'relatedDiagrams.modal'
                }
                values={{
                  componentId,
                  count: (pins || []).length,
                  pins: (pins || []).join(', '),
                }}
              />
              <ComponentDescription description={component?.description} />
            </Flex>
          </Col>
        </Row>
      </>
    )
  }

  // The diagram is always opened for a real connector: the one the picked pins
  // resolved to, with that connector's own pin numbers.
  const selectSubsystem =
    ({ diagramComponentId, diagramPins }) =>
    ({ record }) => {
      dispatch(unsetRelatedDiagramsModal())
      dispatch(
        setGoBackToVehicleTest({ pathname: history.location.pathname, vin }),
      )
      history.push({
        pathname: `/functional-diagrams/vin/${vin}`,
        search: `?${diagramSearchParams({
          subsystemId: record.id,
          componentId: diagramComponentId,
          pins: diagramPins,
        })}`,
      })
    }

  // Resolving a pin selection builds the vehicle's detailed graph, so this is a
  // noticeably longer wait than the plain component search — worth a spinner rather
  // than an empty table.
  const content = () => (
    // `.content-container` has no padding of its own and is shared by every
    // full-screen modal, so the inset lives here. 20px matches `.header-container`,
    // which lines the tables up with the title above them.
    <div style={{ padding: '16px 20px' }}>
      <Loadable showText={false} isLoading={isLoading}>
        {groups()}
      </Loadable>
    </div>
  )

  const groups = () => {
    // No pins picked — the component is the subject, exactly as before. This is
    // also the harness-view tooltip's path.
    if (!connectors) {
      return (
        <SubsystemsViewTable
          susbsystemCallback={selectSubsystem({
            diagramComponentId: componentId,
            diagramPins: pins,
          })}
          subsystems={searchSubsystems}
          emptyText={
            <Flex vertical gap="small" align="center">
              <Typography.Text>
                <FormattedMessage id="relatedDiagrams.noDiagrams" />
              </Typography.Text>
              <Typography.Text type="secondary">
                <FormattedMessage id="relatedDiagrams.noDiagramsHint" />
              </Typography.Text>
            </Flex>
          }
        />
      )
    }

    // Each group is a table with its own header, so they need air between them to
    // read as separate answers rather than one long list.
    return (
      <Flex vertical gap="middle">
        {connectors.map((connector) => (
          <ConnectorGroup
            key={connector.connectorId}
            connector={connector}
            subsystems={(searchSubsystems || []).filter(
              (subsystem) => subsystem.connectorId === connector.connectorId,
            )}
            onSelect={selectSubsystem({
              diagramComponentId: connector.connectorId,
              diagramPins: connector.pins,
            })}
          />
        ))}
        {unresolved.length > 0 && (
          <Typography.Text type="secondary" data-testid="unresolved-pins">
            <FormattedMessage
              id="relatedDiagrams.unresolvedPins"
              values={{ pins: unresolved.join(', ') }}
            />
          </Typography.Text>
        )}
      </Flex>
    )
  }

  return (
    <ModalFullScreen
      HeaderProp={header}
      ContentProp={content}
      isOpen={isOpen}
    />
  )
}
